import { camelCase, isPlainObject } from 'lodash-es'

/**
 * How many levels of keys under each field are data, not field names.
 *
 * `convenienceFees` is keyed by pricing category (`VISA`, `ACH`), and `convenienceFeesByProfile` by
 * fee profile (`DEFAULT`, `MERCHANT_UI`) and then pricing category. Snake-casing those would turn
 * `MERCHANT_UI` into `_m_e_r_c_h_a_n_t__u_i`, which no reader looks up.
 */
const DATA_KEY_LEVELS = {
  convenienceFees: 1,
  convenienceFeesByProfile: 2
}

function snakeCase(key) {
  return key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
}

function convert(value, dataLevels = 0) {
  if (Array.isArray(value)) {
    return value.map(item => convert(item, dataLevels))
  }

  if (!isPlainObject(value)) {
    return value
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, child]) =>
      dataLevels > 0
        ? [key, convert(child, dataLevels - 1)]
        : [snakeCase(key), convert(child, DATA_KEY_LEVELS[key] || 0)]
    )
  )
}

/**
 * Core-service's merchant record in the snake_case shape Django's merchant retrieve returned.
 *
 * `currentMerchant` is read in that shape across the app, so this is the only place the core
 * record's camelCase is seen. Field names are converted at every depth; the pricing-category and
 * fee-profile keys of the two convenience-fee maps are data and pass through unchanged.
 *
 * @param {object} record The body of `GET /core/v1/merchant/{pkid}/record`.
 * @returns {object} The same record with Django's key names.
 */
export function toDjangoMerchantShape(record) {
  return convert(record)
}

/** Core-service refused or failed the settings save; its body is one message, not a field map. */
export class MerchantSettingsSaveError extends Error {
  constructor(message) {
    super(message)
    this.name = 'MerchantSettingsSaveError'
  }
}

const SETTINGS_FIELDS = [
  'require_cvv',
  'allow_waiving_fees',
  'allow_group_message',
  'is_automation_active',
  'check_custom_roles',
  'enable_payment_routing',
  'days_past_due',
  'autopay_continuation',
  'promise_continuation',
  'hide_multicard',
  'show_balance_to_customer',
  'customer_level_payment_rules'
]

// core-service refuses a change to these from anyone but a super.
const SUPER_ONLY_SETTINGS_FIELDS = [
  'merchant_only_autopay',
  'enable_autopay_retry',
  'enable_inbox_filtering',
  'enable_custom_payments',
  'timezone',
  'autopay_execution_time',
  'default_location_id'
]

/**
 * The camelCase body of `PUT /core/v1/merchant/{pkid}/record` for the General Settings form.
 * @param {object} settings The form's snake_case settings; keys other than the settings are dropped.
 * @param {{ superUser: boolean }} options False leaves out the super-only fields, as the screen does.
 * @returns {object} The body, `daysPastDue` a number although the input holds a string.
 */
export function toMerchantRecordUpdate(settings, { superUser }) {
  const fields = superUser
    ? [...SETTINGS_FIELDS, ...SUPER_ONLY_SETTINGS_FIELDS]
    : SETTINGS_FIELDS
  const body = Object.fromEntries(
    fields.map(field => [camelCase(field), settings[field]])
  )
  body.daysPastDue = Number(body.daysPastDue)

  return body
}
